/**
 * The dispatch commands that have been sent and may not have been answered (batch 8 T12c1, #3033): enqueue of a purchase order dispatch.
 *
 * An enqueue has a durable identity: the `operationKey` is part of the body, and the server replays an identical request under the same
 * key and refuses a different one. That only helps if a repeat is the same key with the same body for the same order, so the exact
 * command is written down BEFORE it is sent and kept until the server has decided:
 *
 *  - accepted (2xx, also a replay): removed;
 *  - refused (a 4xx the server read): removed; the user reviews again and a new command gets a new key;
 *  - uncertain (lost connection, timeout, 5xx, expired session): KEPT. The screen offers to send the identical command again, or to
 *    abandon it knowingly. The same intent is never sent under a fresh key, because if the first attempt committed that would create
 *    a second dispatch.
 *
 * Ownership. An entry belongs to the session that wrote it (tenant, user, session family; never a token or the in-memory epoch) and
 * lives in a document named after that owner. Another tenant, another user, a new login or a signed-out page cannot find it,
 * overwrite it or delete it. A reload of the same session finds it again with the exact key and body. Removal is compare-and-delete.
 *
 * Storage is `sessionStorage` (per tab), bounded (entries, characters) and every entry is validated on read. A write counts only when it
 * was read back unchanged: a browser that refuses it, or silently keeps nothing, is reported (`unavailable`) and the command is NOT
 * started (`not_durable`): durable-before-send is a precondition of the request, not a best effort. Entries that were durable
 * earlier and are unresolved stay in memory for this page (never dropped, never claimed durable) and are written back as soon as
 * storage works again. A document that cannot be read back is `corrupt`: nothing in it is used and no new command starts until the
 * user knowingly discards the damaged record. No token, no credential and no recipient address is ever written.
 *
 * Nothing here ever sends a request.
 */

import { useCallback, useSyncExternalStore } from 'react';
import { ownerEquals, ownerId, type DispatchOwner } from './dispatchSession';
import type { DispatchChannel, DispatchEnqueueBody } from './dispatchTypes';

export type JournalStatus = 'ok' | 'unavailable' | 'corrupt';
/**
 * `not_durable`: the exact command could not be written down and read back, so it was NOT kept and must NOT be sent.
 */
export type PutStatus = 'stored' | 'not_durable' | 'conflict' | 'full' | 'corrupt';

export interface JournalEntry {
  /** The address within the owner's journal: one unresolved enqueue per purchase order. */
  readonly slot: string;
  readonly purchaseOrderId: string;
  readonly owner: DispatchOwner;
  /** The exact body, never rebuilt, never edited. */
  readonly body: DispatchEnqueueBody;
  /** What the user reviewed (for display only). */
  readonly documentVersion: number;
  readonly startedAt: string;
  /** How many times this very command was sent. */
  readonly attempts: number;
  /** In-memory generation it was sent under; `null` after a reload (the epoch is not persisted). */
  readonly epoch: number | null;
}

export interface JournalView {
  readonly status: JournalStatus;
  readonly entries: readonly JournalEntry[];
  /** Slots with a command on its way in this page (possibly started by a screen that has since unmounted). */
  readonly inFlight: ReadonlySet<string>;
}

export const MAX_JOURNAL_ENTRIES = 20;
export const MAX_JOURNAL_CHARS = 64 * 1024;
const PREFIX = 'os-buddy:po-dispatch-journal:v1:';

export const slotOf = (purchaseOrderId: string): string => `enqueue:${purchaseOrderId}`;

export const EMPTY_VIEW: JournalView = Object.freeze({
  status: 'ok',
  entries: [],
  inFlight: new Set<string>(),
});

// ---------------------------------------------------------------------------
// Validation of what is read back
// ---------------------------------------------------------------------------

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const isText = (value: unknown, max: number): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= max;
const SHA256 = /^[0-9a-f]{64}$/;
const CHANNELS: readonly string[] = ['email', 'api'];

function validBody(body: unknown): body is DispatchEnqueueBody {
  return (
    isRecord(body) &&
    Object.keys(body).length === 3 &&
    typeof body['channel'] === 'string' &&
    CHANNELS.includes(body['channel']) &&
    isText(body['operationKey'], 200) &&
    body['operationKey'] === body['operationKey'].trim() &&
    typeof body['reviewedSnapshotSha256'] === 'string' &&
    SHA256.test(body['reviewedSnapshotSha256'])
  );
}

interface StoredEntry {
  purchaseOrderId: string;
  body: DispatchEnqueueBody;
  documentVersion: number;
  startedAt: string;
  attempts: number;
}

function parseDocument(raw: string, owner: DispatchOwner): StoredEntry[] | null {
  if (raw.length > MAX_JOURNAL_CHARS) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed['v'] !== 1 || !isRecord(parsed['owner'])) return null;
  const claimed = parsed['owner'];
  if (
    !ownerEquals(owner, {
      tenantId: String(claimed['tenantId']),
      userId: String(claimed['userId']),
      sessionFamilyId: String(claimed['sessionFamilyId']),
    })
  ) {
    return null;
  }
  const entries = parsed['entries'];
  if (!Array.isArray(entries) || entries.length > MAX_JOURNAL_ENTRIES) return null;
  const slots = new Set<string>();
  const result: StoredEntry[] = [];
  for (const entry of entries as unknown[]) {
    if (!isRecord(entry)) return null;
    const { purchaseOrderId, body, documentVersion, startedAt, attempts } = entry;
    if (!isText(purchaseOrderId, 100) || !validBody(body)) return null;
    if (
      typeof documentVersion !== 'number' ||
      !Number.isSafeInteger(documentVersion) ||
      documentVersion < 1
    )
      return null;
    if (typeof startedAt !== 'string' || Number.isNaN(Date.parse(startedAt))) return null;
    if (
      typeof attempts !== 'number' ||
      !Number.isSafeInteger(attempts) ||
      attempts < 0 ||
      attempts > 1000
    )
      return null;
    const slot = slotOf(purchaseOrderId);
    if (slots.has(slot)) return null;
    slots.add(slot);
    result.push({ purchaseOrderId, body, documentVersion, startedAt, attempts });
  }
  return result;
}

// ---------------------------------------------------------------------------
// The books: one per owner, in memory, backed by sessionStorage
// ---------------------------------------------------------------------------

interface Book {
  entries: Map<string, JournalEntry>;
  inFlight: Map<string, string>;
  /** What storage held when this book last agreed with it. `undefined`: never read. */
  lastRaw: string | null | undefined;
  unreadable: boolean;
  writeFailed: boolean;
  corrupt: boolean;
  view: JournalView | null;
}

const books = new Map<string, Book>();
const listeners = new Set<() => void>();

const addressOf = (owner: DispatchOwner): string =>
  `${PREFIX}${[owner.tenantId, owner.userId, owner.sessionFamilyId].map(encodeURIComponent).join(':')}`;

function notify(): void {
  for (const listener of [...listeners]) listener();
}

function bookOf(owner: DispatchOwner): Book {
  const id = ownerId(owner);
  let book = books.get(id);
  if (!book) {
    book = {
      entries: new Map(),
      inFlight: new Map(),
      lastRaw: undefined,
      unreadable: false,
      writeFailed: false,
      corrupt: false,
      view: null,
    };
    books.set(id, book);
  }
  return book;
}

/** Brings the book in line with storage when storage changed under it (another script, a cleared store). */
function refresh(owner: DispatchOwner, book: Book): void {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(addressOf(owner));
  } catch {
    if (!book.unreadable) {
      book.unreadable = true;
      book.view = null;
    }
    return;
  }
  const wasUnreadable = book.unreadable;
  if (wasUnreadable) {
    book.unreadable = false;
    book.view = null;
  }
  // Entries that only this page holds (storage was refused, or silently lost the record) are written back once storage works again,
  // never dropped: a command that may have left is still unresolved.
  if (
    raw === null &&
    book.entries.size > 0 &&
    (wasUnreadable || book.writeFailed || book.lastRaw !== null)
  ) {
    write(owner, book);
    return;
  }
  if (book.lastRaw === raw) return;
  book.lastRaw = raw;
  book.view = null;
  book.writeFailed = false;
  if (raw === null) {
    book.entries = new Map();
    book.corrupt = false;
    return;
  }
  const stored = parseDocument(raw, owner);
  if (stored === null) {
    book.entries = new Map();
    book.corrupt = true;
    return;
  }
  book.corrupt = false;
  const previous = book.entries;
  const next = new Map<string, JournalEntry>();
  for (const entry of stored) {
    const slot = slotOf(entry.purchaseOrderId);
    const known = previous.get(slot);
    const same = known !== undefined && known.body.operationKey === entry.body.operationKey;
    next.set(slot, {
      slot,
      purchaseOrderId: entry.purchaseOrderId,
      owner,
      // The body object of a key this page already holds stays the same object.
      body: same ? known.body : entry.body,
      documentVersion: entry.documentVersion,
      startedAt: entry.startedAt,
      attempts: entry.attempts,
      epoch: same ? known.epoch : null,
    });
  }
  book.entries = next;
}

/** Writes the entries. Returns false (and remembers it) when the browser would not keep them. */
function write(owner: DispatchOwner, book: Book): boolean {
  const address = addressOf(owner);
  const before = `${book.writeFailed}:${book.unreadable}`;
  try {
    if (book.entries.size === 0) {
      window.sessionStorage.removeItem(address);
      book.lastRaw = null;
    } else {
      const text = JSON.stringify({
        v: 1,
        owner: {
          tenantId: owner.tenantId,
          userId: owner.userId,
          sessionFamilyId: owner.sessionFamilyId,
        },
        entries: [...book.entries.values()].map((entry) => ({
          purchaseOrderId: entry.purchaseOrderId,
          body: entry.body,
          documentVersion: entry.documentVersion,
          startedAt: entry.startedAt,
          attempts: entry.attempts,
        })),
      });
      if (text.length > MAX_JOURNAL_CHARS) throw new Error('journal document too large');
      window.sessionStorage.setItem(address, text);
      if (window.sessionStorage.getItem(address) !== text)
        throw new Error('journal document not kept');
      book.lastRaw = text;
    }
    book.writeFailed = false;
    return true;
  } catch {
    book.writeFailed = true;
    try {
      book.lastRaw = window.sessionStorage.getItem(address);
    } catch {
      book.unreadable = true;
    }
    return false;
  } finally {
    // A write that fails again must not make every snapshot a new object.
    if (before !== `${book.writeFailed}:${book.unreadable}`) book.view = null;
  }
}

export function getJournalView(owner: DispatchOwner | null): JournalView {
  if (owner === null) return EMPTY_VIEW;
  const book = bookOf(owner);
  refresh(owner, book);
  if (book.view === null) {
    const status: JournalStatus = book.corrupt
      ? 'corrupt'
      : book.unreadable || book.writeFailed
        ? 'unavailable'
        : 'ok';
    book.view = {
      status,
      entries: [...book.entries.values()],
      inFlight: new Set(book.inFlight.keys()),
    };
  }
  return book.view;
}

export function subscribeJournal(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The journal of `owner`, live: re-renders when an entry, a flight or the storage state changes. */
export function useJournalView(owner: DispatchOwner | null): JournalView {
  const snapshot = useCallback(() => getJournalView(owner), [owner]);
  return useSyncExternalStore(subscribeJournal, snapshot, snapshot);
}

export interface NewEntry {
  purchaseOrderId: string;
  channel: DispatchChannel;
  body: DispatchEnqueueBody;
  documentVersion: number;
  epoch: number;
}

/**
 * Writes the command down BEFORE it is sent, as its first attempt, and reads it back. Never overwrites a different unresolved command of
 * the same order. When the browser would not keep the exact command (`not_durable`) nothing is left behind: the command was never sent,
 * so no entry may suggest that it was, and the caller must not send it.
 */
export function putEntry(owner: DispatchOwner, input: NewEntry): PutStatus {
  const book = bookOf(owner);
  refresh(owner, book);
  if (book.corrupt) return 'corrupt';
  const slot = slotOf(input.purchaseOrderId);
  if (book.entries.has(slot)) return 'conflict';
  if (book.entries.size >= MAX_JOURNAL_ENTRIES) return 'full';
  book.entries.set(slot, {
    slot,
    purchaseOrderId: input.purchaseOrderId,
    owner,
    body: input.body,
    documentVersion: input.documentVersion,
    startedAt: new Date().toISOString(),
    // Counted in the very write that makes the command durable: there is no stored command that was never sent.
    attempts: 1,
    epoch: input.epoch,
  });
  const stored = write(owner, book);
  if (!stored) {
    book.entries.delete(slot);
    // Best effort: leave storage without the command, as it was. The failure stays on record until a write works again.
    write(owner, book);
    book.writeFailed = true;
  }
  book.view = null;
  notify();
  return stored ? 'stored' : 'not_durable';
}

/**
 * Counts one more send (or takes one back, when the client refused before anything left). Returns whether the new count was written and
 * read back: a send is only allowed when it was. A count that could not be kept is not applied, and the entry stays as it was.
 */
export function recordAttempt(
  owner: DispatchOwner,
  slot: string,
  key: string,
  delta: 1 | -1
): boolean {
  const book = bookOf(owner);
  const entry = book.entries.get(slot);
  if (!entry || entry.body.operationKey !== key) return false;
  const attempts = Math.max(entry.attempts + delta, 0);
  book.entries.set(slot, { ...entry, attempts });
  const stored = write(owner, book);
  if (!stored && delta === 1) book.entries.set(slot, entry);
  book.view = null;
  notify();
  return stored;
}

/** Compare-and-delete: only the entry that still holds `key` is removed. */
export function removeEntry(owner: DispatchOwner, slot: string, key: string): boolean {
  const book = bookOf(owner);
  const entry = book.entries.get(slot);
  if (!entry || entry.body.operationKey !== key) return false;
  book.entries.delete(slot);
  write(owner, book);
  book.view = null;
  notify();
  return true;
}

/** The user knowingly discarded a damaged record: its content was never used. */
export function dropCorruptJournal(owner: DispatchOwner): void {
  const book = bookOf(owner);
  refresh(owner, book);
  if (!book.corrupt) return;
  try {
    window.sessionStorage.removeItem(addressOf(owner));
    book.lastRaw = null;
  } catch {
    // Still unreadable: the notice stays.
    return;
  }
  book.corrupt = false;
  book.entries = new Map();
  book.view = null;
  notify();
}

/** Marks a command as on its way. False when another command of the slot already is. */
export function beginFlight(owner: DispatchOwner, slot: string, key: string): boolean {
  const book = bookOf(owner);
  if (book.inFlight.has(slot)) return false;
  book.inFlight.set(slot, key);
  book.view = null;
  notify();
  return true;
}

export function endFlight(owner: DispatchOwner, slot: string, key: string): void {
  const book = bookOf(owner);
  if (book.inFlight.get(slot) !== key) return;
  book.inFlight.delete(slot);
  book.view = null;
  notify();
}

/** Test support: forgets every book and every stored document. */
export function resetDispatchJournalForTests(): void {
  books.clear();
  resetStored();
  notify();
}

function resetStored(): void {
  try {
    const stale: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith(PREFIX)) stale.push(key);
    }
    for (const key of stale) window.sessionStorage.removeItem(key);
  } catch {
    // Nothing stored, nothing to remove.
  }
}

/** Test support: a page reload. The memory of the page is gone, the documents in `sessionStorage` stay. */
export function resetDispatchJournalMemoryForReload(): void {
  books.clear();
  notify();
}
